/**
 * @voltxampere/math — Sparse LU adapter
 *
 * Sprint 4 ships a "simple" sparse LU: convert to dense, factor with
 * the dense LU primitive, return wrapped factors. The public surface
 * mirrors what a real sparse solver (KLU/UMFPACK-style) will expose,
 * so Sprint 10+ can swap the implementation without breaking
 * downstream callers.
 *
 * Why ship the adapter at all? Sprint 5's DC solver wants to compose
 * `denseToSparse(stamp(...))` into a `solve(...)` call without
 * branching on storage format. Having `sparseLuSolve(sparseMatrix, b)`
 * available removes that branch from the solver code path now.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ContractViolationError } from "@voltxampere/shared";
import { sparseToDense } from "../sparse/csr.js";
import { luDecompose, luSolve } from "./dense-lu.js";
function assertSquare(m, op) {
    if (m.rows !== m.cols) {
        throw new ContractViolationError(`${op}: sparse matrix must be square (got ${String(m.rows)}x${String(m.cols)})`);
    }
}
/**
 * Factor a sparse matrix. Sprint 4 implementation densifies first;
 * Sprint 10+ will replace this with a true sparse algorithm (KLU
 * column ordering + symbolic + numeric phases) without changing the
 * return type beyond adding fields.
 */
export function sparseLuDecompose(m) {
    assertSquare(m, "sparseLuDecompose");
    const dense = sparseToDense(m);
    return { dense: luDecompose(dense), rows: m.rows, cols: m.cols };
}
/** Solve `m · x = b` with a previously-computed sparse factorisation. */
export function sparseLuSolve(lu, b) {
    if (b.length !== lu.rows) {
        throw new ContractViolationError(`sparseLuSolve: rhs length ${String(b.length)} ≠ matrix rank ${String(lu.rows)}`);
    }
    return luSolve(lu.dense, b);
}
/** Convenience: factor and solve in one call. */
export function sparseSolve(m, b) {
    return sparseLuSolve(sparseLuDecompose(m), b);
}
//# sourceMappingURL=sparse-lu.js.map